// Sucessão da cadeia de gestores no client (SUC-01/03/04/09, MOD-06) — regras
// canônicas 2026-07-14/15. O backend (next_managers v2.1) já resolve SUC-01 e
// entrega, por nível, a lista completa `nivelN_duplas[]` com a sugestão marcada
// `suggested: true`; aqui o client apenas materializa as opções do SelectDupla,
// expande gestores achados na busca manual (gestores_mad) e traduz a opção
// escolhida nos papéis de escrita da task (SUC-09). Nenhum import de React.

import { DuplaOption, ManagerRef, NextManagersV2 } from '../shared/types';
import { isSemDupla } from './context';

export type SuccessionLevel = 1 | 2 | 3 | 'prosseguir';

// Opções de dupla para um nível da cadeia (SUC-04/06). Fonte primária:
// `nivelN_duplas[]` do next_managers v2.1 (sugestão já marcada pelo backend).
// Fallback (E15 — resposta v2 sem a lista): se o nível existe mas a lista não,
// o gestor sugerido vira opção SOLO suggested — dupla é opcional (SUC-08),
// nunca anomalia.
export function buildDuplaOptions(nm: NextManagersV2 | null | undefined, level: SuccessionLevel): DuplaOption[] {
  if (!nm) return [];

  const prefix = level === 'prosseguir' ? 'prosseguir' : `nivel${level}`;

  const duplas = (nm as any)[`${prefix}_duplas`];

  if (Array.isArray(duplas) && duplas.length > 0) {
    return duplas as DuplaOption[];
  }

  const manager = (nm as any)[prefix] as ManagerRef | undefined;

  if (manager && manager._id) {
    return [{ gestor: manager, parceiro: null, suggested: true }];
  }

  return [];
}

// Expansão de um gestor achado na BUSCA MANUAL (aggregate gestores_mad — DAD-08)
// em opções (MOD-06/SUC-04): a opção INDIVIDUAL (solo) vem SEMPRE, em primeiro —
// a dupla é opcional e o gestor pode apreciar sozinho (requisito reafirmado
// 2026-07-21) — seguida de uma opção-par por parceiro da mad_dupla. Gestor em
// EXCEÇÃO de dupla — COE (COE-01) ou uor_linha isento (regra 2026-07-21) — NUNCA
// tem dupla: mesmo que venha mad_dupla residual, a expansão é somente solo.
// Nenhuma opção da busca manual vem sugerida (a sugestão é exclusiva de SUC-01).
export function expandManagerToDuplaOptions(manager: ManagerRef | null | undefined): DuplaOption[] {
  if (!manager || !manager._id) return [];

  const solo: DuplaOption[] = [{ gestor: manager, parceiro: null }];

  if (isSemDupla(manager)) return solo;

  const madDupla = (manager as any).mad_dupla;

  if (!Array.isArray(madDupla)) return solo;

  const pares: DuplaOption[] = madDupla
    .filter((md: any) => !!md?._id)
    .map((md: any) => ({ gestor: manager, parceiro: md as ManagerRef }));

  return [...solo, ...pares];
}

export interface SelectionWrite {
  evaluatorId: string;
  nonEvaluatorId: string | null;
}

// Tradução da opção confirmada nos papéis de escrita da task (SUC-09): o GESTOR
// da opção entra em `evaluator[]`; o PARCEIRO entra em `non_evaluator[]` (null na
// opção solo). Funcionalmente equivalentes — a dupla age como um (R-21) e o swap
// CON-05 registra quem de fato agiu. Opção sem gestor é dado inválido → null
// (chamador decide; ver logInvalidChain).
export function resolveSelectionWrite(option: DuplaOption | null | undefined): SelectionWrite | null {
  if (!option || !option.gestor || !option.gestor._id) return null;

  return {
    evaluatorId: String(option.gestor._id),
    nonEvaluatorId: option.parceiro && option.parceiro._id ? String(option.parceiro._id) : null,
  };
}

// Opção pré-selecionada do SelectDupla: a única marcada `suggested` (SUC-01 via
// backend). Ausente → null (sem sugestão automática — seleção manual/aviso).
export function suggestedOption(options: DuplaOption[] | null | undefined): DuplaOption | null {
  if (!Array.isArray(options)) return null;

  return options.find(option => option.suggested === true) ?? null;
}

// DIAG-01 (REVISTA 2026-07-15): telemetria SOMENTE para DADO INVÁLIDO — elo da
// cadeia sem `gestor` (impossível derivar o próximo) ou referência quebrada.
// Gestor sem dupla e promoção sem pit stop são fluxo normal e NÃO logam.
// console.warn estruturado, sem travar o fluxo.
export function logInvalidChain(task: any, reason: string): void {
  // eslint-disable-next-line no-console
  console.warn('[apreciar-mad][DIAG-01] cadeia com dado inválido', {
    taskId: task?._id ?? null,
    owner: task?.owner ?? null,
    reason,
  });
}
